import { morph, MORPH_OPTIONS } from './dom-morph.js';
import { cartPerformance } from './performance-component.js';

const SECTION_ID_PREFIX = 'shopify-section-';

class SectionRenderer {
  cache = new Map();
  abortControllersBySectionId = new Map();
  pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', () => this.cachePageSections());
  }

  async renderSection(sectionId, options = {}) {
    const { cache = !window.Shopify?.designMode, mode = 'full', url = new URL(window.location.href) } = options;
    this.abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.abortControllersBySectionId.set(sectionId, abortController);

    try {
      const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

      if (!abortController.signal.aborted) {
        morphSection(sectionId, sectionHTML, mode);
      }

      return sectionHTML;
    } finally {
      if (this.abortControllersBySectionId.get(sectionId) === abortController) {
        this.abortControllersBySectionId.delete(sectionId);
      }
    }
  }

  abortPendingMorph(sectionId) {
    this.abortControllersBySectionId.get(sectionId)?.abort();
  }

  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    const pendingPromise = this.pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }

    const nextPromise = fetch(sectionUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then((response) => {
      if (!response.ok) throw new Error(`Section render failed: ${sectionId}`);
      return response.text();
    }).finally(() => {
      this.pendingPromises.delete(sectionUrl);
    });

    this.pendingPromises.set(sectionUrl, nextPromise);

    const sectionHTML = await nextPromise;
    this.cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  clear() {
    this.cache.clear();
    this.pendingPromises.clear();
  }

  cachePageSections() {
    document.querySelectorAll('.shopify-section[id]').forEach((section) => {
      if (containsShadowRoot(section)) return;
      const url = buildSectionRenderingURL(section.id);
      if (!this.cache.has(url)) this.cache.set(url, section.outerHTML);
    });
  }
}

export function normalizeSectionId(sectionId) {
  return String(sectionId || '').replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${normalizeSectionId(sectionId)}`;
}

export function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  const requestUrl = new URL(url.toString(), window.location.origin);
  requestUrl.searchParams.set('section_id', normalizeSectionId(sectionId));
  requestUrl.searchParams.sort();
  return requestUrl.toString();
}

function containsShadowRoot(element) {
  return Boolean(element.shadowRoot) || Array.from(element.children).some(containsShadowRoot);
}

function syncHydrationRootAttributes(existingElement, newElement) {
  if (!(existingElement instanceof Element) || !(newElement instanceof Element)) return;

  const wasOpen = existingElement instanceof HTMLDialogElement ? existingElement.open : false;
  const wasVisible = existingElement.classList.contains('is-open');
  const ariaHidden = existingElement.getAttribute('aria-hidden');

  Array.from(newElement.attributes).forEach((attribute) => {
    if (attribute.name === 'open' || attribute.name === 'aria-hidden') return;
    existingElement.setAttribute(attribute.name, attribute.value);
  });

  Array.from(existingElement.attributes).forEach((attribute) => {
    if (attribute.name === 'open' || attribute.name === 'aria-hidden') return;
    if (!newElement.hasAttribute(attribute.name)) existingElement.removeAttribute(attribute.name);
  });

  if (wasVisible) existingElement.classList.add('is-open');
  if (ariaHidden !== null) existingElement.setAttribute('aria-hidden', ariaHidden);
  if (existingElement instanceof HTMLDialogElement && wasOpen) {
    existingElement.open = true;
    existingElement.setAttribute('open', '');
  }
}

function hydrationRoot(element, sectionId) {
  if (!(element instanceof Element)) return element;

  const selector = `[data-section-id="${CSS.escape(sectionId)}"]`;
  return element.matches(selector) ? element : element.querySelector(selector) || element;
}

export function morphSection(sectionId, html, mode = 'full') {
  if (!sectionId || !html) return false;
  const morphMarker = cartPerformance.createStartingMarker('section:morph');

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const normalizedSectionId = normalizeSectionId(sectionId);
  const existingElement =
    document.getElementById(buildSectionSelector(normalizedSectionId)) ||
    document.querySelector(`[data-section-id="${CSS.escape(normalizedSectionId)}"]`);
  const newElement =
    parsed.getElementById(buildSectionSelector(normalizedSectionId)) ||
    parsed.querySelector(`[data-section-id="${CSS.escape(normalizedSectionId)}"]`);

  if (!existingElement || !newElement) return false;
  if (mode === 'hydration') {
    syncHydrationRootAttributes(
      hydrationRoot(existingElement, normalizedSectionId),
      hydrationRoot(newElement, normalizedSectionId),
    );
  }

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    childrenOnly: mode !== 'full',
    hydrationMode: mode === 'hydration',
  });
  cartPerformance.measureFromMarker(morphMarker);

  window.dispatchEvent(
    new CustomEvent('shopify:section-rendered', {
      detail: { sectionId: normalizedSectionId },
    }),
  );

  return true;
}

export function morphSections(sections = {}, modeBySection = {}) {
  Object.entries(sections || {}).forEach(([sectionId, html]) => {
    const mode = modeBySection[sectionId] || 'full';
    morphSection(sectionId, html, mode);
  });
}

export const sectionRenderer = new SectionRenderer();

window.MedistikSectionRenderer = {
  sectionRenderer,
  morphSection,
  morphSections,
  normalizeSectionId,
  buildSectionSelector,
};
